import omit from 'lodash/omit';
import pick from 'lodash/pick';

import {
  RIGHTS_MODULES,
  RIGHTS_ANALYTICS,
  RIGHTS_PAYMENT_ACCOUNTS,
  RIGHTS_CHAINS,
  RIGHTS_BRANCHES,
  RIGHTS_USERS,
  RIGHTS_CLIENTS,
  RIGHTS_RECORDS,
  RIGHTS_LEGAL_ENTITIES,
  RIGHTS_INTEGRATIONS,
  RIGHTS_SALE_PLANS,
  RIGHTS_SALES_REGISTER,
  RIGHTS_ACCESS_RIGHTS_TEMPLATES,
  RIGHTS_CLIENT_SERVICES,
  RIGHTS_POSITIONS,
  RIGHTS_DATA_MIGRATIONS,
  RIGHTS_LEADS,
  RIGHTS_WORK_SCHEDULES,
  RIGHTS_SALES_FUNNELS,
  RIGHTS_HISTORY,
  RIGHTS_CRM_TASKS,
  RIGHTS_SENDOUTS,
  RIGHTS_IS_ON,
  RIGHTS_IS_CREATE,
  RIGHTS_IS_DELETE,
  RIGHTS_IS_EDIT,
  RIGHTS_IS_CREATE_SERVICES,
  RIGHTS_IS_EDIT_SERVICES,
  RIGHTS_IS_EMPLOYEES_REGISTER,
  RIGHTS_IS_COMMUNICATE,
  RIGHTS_IS_EXPORT,
  RIGHTS_IS_SHOW_CONTACTS,
  RIGHTS_IS_VIEW_COMMUNICATION,
  RIGHTS_IS_FINANCIAL_STATISTICS,
  RIGHTS_IS_JOURNAL,
  RIGHTS_IS_PAY_RECORD,
  RIGHTS_IS_REGISTER,
  RIGHTS_IS_STATUSES,
  RIGHTS_IS_VIEW_ALL_RECORDS,
  RIGHTS_IS_CREATE_COLLECTION,
  RIGHTS_IS_CREATE_EXPENSE,
  RIGHTS_IS_DELETE_COLLECTION,
  RIGHTS_IS_DELETE_EXPENSE,
  RIGHTS_IS_DELETE_SALE,
  RIGHTS_IS_TOP_CLIENT_BALANCE,
  RIGHTS_IS_TOPPING_UP_CASH,
  RIGHTS_IS_CREATE_TEMPLATES,
  RIGHTS_IS_DELETE_TEMPLATES,
  RIGHTS_IS_ATTACH,
  RIGHTS_IS_CATEGORY_CREATE,
  RIGHTS_IS_CATEGORY_DELETE,
  RIGHTS_IS_CATEGORY_EDIT,
  RIGHTS_IS_CLIENT_SERVICES,
  RIGHTS_IS_IMPORT,
  RIGHTS_IS_SERVICE_CATEGORIES,
  RIGHTS_IS_ALLOW_SPAM,
  RIGHTS_IS_OWNER_CHANGE,
  RIGHTS_IS_SALE_FUNNEL_CHANGE,
  RIGHTS_IS_TAGS_CHANGE,
  RIGHTS_IS_TAKE,
  RIGHTS_IS_VIEW_ALL,
  RIGHTS_IS_DELETE_PAST,
  RIGHTS_IS_EDIT_PAST,
  RIGHTS_IS_FUNNEL_CREATE,
  RIGHTS_IS_FUNNEL_DELETE,
  RIGHTS_IS_FUNNEL_EDIT,
  RIGHTS_IS_STAGE_CREATE,
  RIGHTS_IS_STAGE_DELETE,
  RIGHTS_IS_STAGE_EDIT,
  RIGHTS_IS_ASSIGN,
  RIGHTS_IS_CHANGE_STATUS,
  RIGHTS_IS_EDIT_ALL,
  RIGHTS_IS_CHANGE_BRANCHES,
  RIGHTS_IS_CHANGE_OWNER,
  RIGHTS_IS_USER,
  RIGHTS_AI,
  RIGHTS_IS_CONTROL,
  RIGHTS_IS_VIEW_RECEIVERS,
  RIGHTS_ORGANIZATIONS,
  RIGHTS_SECRET_LOGIN_TO_ORGANIZATION_ACCOUNT,
  RIGHTS_IS_EDITING_RIGHTS_WITHIN_OPEN_RIGHTS,
  RIGHTS_MESSAGE_TEMPLATES,
  RIGHTS_IS_RETURN_MONEY,
  RIGHTS_IS_VIEW_REMINDERS,
  RIGHTS_SALARIES,
  RIGHTS_WAREHOUSE,
  RIGHTS_PRODUCTS,
  RIGHTS_IS_EDIT_REMINDERS,
} from '@app/constants/accessRights';
import type { NAccessRights } from '@app/types';

const generalEntityRights = {
  [RIGHTS_IS_ON]: false,
};

const typicalSpecificRights = {
  ...generalEntityRights,
  [RIGHTS_IS_CREATE]: false,
  [RIGHTS_IS_EDIT]: false,
  [RIGHTS_IS_DELETE]: false,
};

const rightsForChains = omit(typicalSpecificRights, [RIGHTS_IS_DELETE]);
const rightsForBranches = omit(typicalSpecificRights, [RIGHTS_IS_DELETE]);

const rightsForUsers = {
  ...generalEntityRights,
  [RIGHTS_IS_CREATE_SERVICES]: false,
  [RIGHTS_IS_EDIT_SERVICES]: false,
  [RIGHTS_IS_EMPLOYEES_REGISTER]: false,

  // На 26.12.2024 в Сваггере не было этих специальных прав, однако сервер возвращал их
  // для загрузки списка прав, а также принимал их на вход при сохранении выбранных прав пользователем
  [RIGHTS_IS_USER]: false,
  [RIGHTS_IS_CHANGE_OWNER]: false,
  [RIGHTS_IS_CHANGE_BRANCHES]: false,
};

const rightsForClients = {
  ...typicalSpecificRights,
  [RIGHTS_IS_EXPORT]: false,
  [RIGHTS_IS_COMMUNICATE]: false,
  [RIGHTS_IS_VIEW_COMMUNICATION]: false,
  [RIGHTS_IS_SHOW_CONTACTS]: false,
};

const rightsForRecords = {
  ...typicalSpecificRights,
  [RIGHTS_IS_EXPORT]: false,
  [RIGHTS_IS_JOURNAL]: false,
  [RIGHTS_IS_REGISTER]: false,
  [RIGHTS_IS_STATUSES]: false,
  [RIGHTS_IS_VIEW_ALL_RECORDS]: false,
  [RIGHTS_IS_PAY_RECORD]: false,
  [RIGHTS_IS_FINANCIAL_STATISTICS]: false,
  [RIGHTS_IS_VIEW_REMINDERS]: false,
  [RIGHTS_IS_EDIT_REMINDERS]: false,
};

const rightsForSalePlans = {
  ...pick(typicalSpecificRights, [RIGHTS_IS_ON, RIGHTS_IS_CREATE]),
  [RIGHTS_IS_EXPORT]: false,
};

const rightsForFinancialOperations = {
  ...generalEntityRights,
  [RIGHTS_IS_DELETE_SALE]: false,
  [RIGHTS_IS_CREATE_EXPENSE]: false,
  [RIGHTS_IS_CREATE_COLLECTION]: false,
  [RIGHTS_IS_TOPPING_UP_CASH]: false,
  [RIGHTS_IS_DELETE_EXPENSE]: false,
  [RIGHTS_IS_DELETE_COLLECTION]: false,
  [RIGHTS_IS_TOP_CLIENT_BALANCE]: false,
  [RIGHTS_IS_RETURN_MONEY]: false,
  [RIGHTS_IS_EXPORT]: false,
};

const rightsForAccessRightTemplates = {
  ...pick(typicalSpecificRights, [RIGHTS_IS_ON, RIGHTS_IS_EDIT]),
  [RIGHTS_IS_CREATE_TEMPLATES]: false,
  [RIGHTS_IS_DELETE_TEMPLATES]: false,
  [RIGHTS_IS_EDITING_RIGHTS_WITHIN_OPEN_RIGHTS]: false,
};

const rightsForClientServices = {
  ...typicalSpecificRights,
  [RIGHTS_IS_CLIENT_SERVICES]: false,
  [RIGHTS_IS_SERVICE_CATEGORIES]: false,
  [RIGHTS_IS_EXPORT]: false,
  [RIGHTS_IS_IMPORT]: false,
  [RIGHTS_IS_CATEGORY_CREATE]: false,
  [RIGHTS_IS_CATEGORY_EDIT]: false,
  [RIGHTS_IS_CATEGORY_DELETE]: false,
  [RIGHTS_IS_ATTACH]: false,
};

const rightsForMigrationYClients = {
  ...pick(typicalSpecificRights, [RIGHTS_IS_ON, RIGHTS_IS_CREATE]),
};

const rightsForLeads = {
  ...pick(typicalSpecificRights, [RIGHTS_IS_ON, RIGHTS_IS_DELETE]),
  [RIGHTS_IS_OWNER_CHANGE]: false,
  [RIGHTS_IS_TAGS_CHANGE]: false,
  [RIGHTS_IS_VIEW_ALL]: false,
  [RIGHTS_IS_ALLOW_SPAM]: false,
  [RIGHTS_IS_TAKE]: false,
  [RIGHTS_IS_SALE_FUNNEL_CHANGE]: false,
  [RIGHTS_IS_COMMUNICATE]: false,
};

const rightsForWorkSchedules = {
  ...typicalSpecificRights,
  [RIGHTS_IS_EDIT_PAST]: false,
  [RIGHTS_IS_DELETE_PAST]: false,
};

const rightsForSaleFunnels = {
  ...generalEntityRights,
  [RIGHTS_IS_FUNNEL_CREATE]: false,
  [RIGHTS_IS_FUNNEL_EDIT]: false,
  [RIGHTS_IS_FUNNEL_DELETE]: false,
  [RIGHTS_IS_STAGE_CREATE]: false,
  [RIGHTS_IS_STAGE_EDIT]: false,
  [RIGHTS_IS_STAGE_DELETE]: false,
};

const rightsForCrmTasks = {
  ...typicalSpecificRights,
  [RIGHTS_IS_VIEW_ALL]: false,
  [RIGHTS_IS_ASSIGN]: false,
  [RIGHTS_IS_EDIT_ALL]: false,
  [RIGHTS_IS_CHANGE_STATUS]: false,
};

const rightsForSendouts = {
  ...generalEntityRights,
  [RIGHTS_IS_CREATE]: false,
  [RIGHTS_IS_CONTROL]: false,
  [RIGHTS_IS_VIEW_RECEIVERS]: false,
};

const rightsForOrganizations = {
  ...generalEntityRights,
  [RIGHTS_SECRET_LOGIN_TO_ORGANIZATION_ACCOUNT]: false,
};

const rightsForWarehouse = {
  IsInventoryComplete: false,
  IsInventoryCompleting: false,
  IsInventoryCreate: false,
  IsInventoryDelete: false,
  IsInventoryEdit: false,
  IsInventoryReports: false,
  IsOn: false,
  IsProductItemsAccept: false,
  IsProductItemsDecomission: false,
  IsProductItemsDelete: false,
  IsProductItemsMove: false,
  IsSupplierCreate: false,
  IsSupplierDelete: false,
  IsSupplierEdit: false,
  IsSuppliers: false,
  IsWarehouseCreate: false,
  IsWarehouseDelete: false,
  IsWarehouseEdit: false,
};

const rightsForProducts = {
  IsBrandCreate: false,
  IsBrandDelete: false,
  IsBrandEdit: false,
  IsBrands: false,
  IsOn: false,
  IsProductCategories: false,
  IsProductCategoryCreate: false,
  IsProductCategoryDelete: false,
  IsProductCategoryEdit: false,
  IsProductCreate: false,
  IsProductDelete: false,
  IsProductEdit: false,
};

export const initialAccessRights: NAccessRights.ModelAccessRights = {
  UserId: null,
  IsAdmin: false,
  IsSuperAdmin: false,
  [RIGHTS_MODULES]: null,
  [RIGHTS_ANALYTICS]: generalEntityRights,
  [RIGHTS_SALARIES]: generalEntityRights,
  [RIGHTS_PAYMENT_ACCOUNTS]: typicalSpecificRights,
  [RIGHTS_CHAINS]: rightsForChains,
  [RIGHTS_BRANCHES]: rightsForBranches,
  [RIGHTS_USERS]: rightsForUsers,
  [RIGHTS_CLIENTS]: rightsForClients,
  [RIGHTS_RECORDS]: rightsForRecords,
  [RIGHTS_LEGAL_ENTITIES]: typicalSpecificRights,
  [RIGHTS_INTEGRATIONS]: typicalSpecificRights,
  [RIGHTS_SALE_PLANS]: rightsForSalePlans,
  [RIGHTS_SALES_REGISTER]: rightsForFinancialOperations,
  [RIGHTS_ACCESS_RIGHTS_TEMPLATES]: rightsForAccessRightTemplates,
  [RIGHTS_CLIENT_SERVICES]: rightsForClientServices,
  [RIGHTS_POSITIONS]: typicalSpecificRights,
  [RIGHTS_DATA_MIGRATIONS]: rightsForMigrationYClients,
  [RIGHTS_LEADS]: rightsForLeads,
  [RIGHTS_WORK_SCHEDULES]: rightsForWorkSchedules,
  [RIGHTS_SALES_FUNNELS]: rightsForSaleFunnels,
  [RIGHTS_HISTORY]: generalEntityRights,
  [RIGHTS_CRM_TASKS]: rightsForCrmTasks,
  [RIGHTS_SENDOUTS]: rightsForSendouts,
  [RIGHTS_AI]: generalEntityRights,
  [RIGHTS_ORGANIZATIONS]: rightsForOrganizations,
  [RIGHTS_MESSAGE_TEMPLATES]: generalEntityRights,
  [RIGHTS_WAREHOUSE]: rightsForWarehouse,
  [RIGHTS_PRODUCTS]: rightsForProducts,
};
